import { Alert, AlertTitle, Box } from '@mui/material';

import {
  CSVImportModal,
  type ColumnType,
} from '@modules/_shared/components/importer/CSVImportModal';
import { IMPORT_USERS } from '@modules/users/userQueries';
import { useLocale } from '@shared/hooks/locale';

type UsersImportProps = {
  open: boolean;
  onImportSuccess: () => void;
  onImporterClose: () => void;
};

const columns = [
  {
    name: 'Import ID',
    key: 'imported_id',
    required: false,
    description: 'The unique identifier of the user',
    data_type: 'string',
  },
  {
    name: 'First Name',
    key: 'first_name',
    required: true,
    description: 'The first name of the user',
    suggested_mappings: ['First Name', 'first_name', 'Firstname'],
    data_type: 'string',
  },
  {
    name: 'Last Name',
    key: 'last_name',
    required: true,
    description: 'The last name of the user',
    suggested_mappings: ['Last Name', 'last_name', 'family name', 'Lastname'],
    data_type: 'string',
  },
  {
    name: 'Emails',
    key: 'emails',
    required: true,
    description: 'The email of the user',
    suggested_mappings: ['Email', 'Emails', 'emails[0].email'],
    data_type: 'string[]',
  },
  {
    name: 'Phone Numbers',
    key: 'phone_numbers',
    required: false,
    description: 'The phone number of the user',
    suggested_mappings: [
      'Phone Number',
      'Phone',
      'Landline',
      'phone_numbers[0].number',
    ],
    data_type: 'string[]',
  },
  {
    name: 'Language',
    key: 'language',
    required: false,
    description: 'The language of the user',
    suggested_mappings: ['Language', 'lang', 'Locale'],
    data_type: 'string',
  },
  {
    name: 'Notes',
    key: 'note',
    required: false,
    description:
      'Markdown text. Multiple notes require --- on its own line; no other delimiters are supported. The last segment appears at the top of the contact feed.',
    suggested_mappings: ['Notes', 'Note', 'Comment', 'Comments', 'Remarks'],
    data_type: 'string',
  },
  {
    name: 'Address Street Name',
    key: 'route',
    required: false,
    description: 'The route of the user',
    suggested_mappings: ['Route', 'Path', 'Line 1', 'Street'],
    data_type: 'string',
  },
  {
    name: 'Address Street Number',
    key: 'street_number',
    required: false,
    description: 'The street number of the user',
    suggested_mappings: ['Street Number', 'Number'],
    data_type: 'string',
  },
  {
    name: 'Address Postal Code',
    key: 'postal_code',
    required: false,
    description: 'The postal code of the user',
    suggested_mappings: ['Postal Code', 'Postcode', 'Zip Code'],
    data_type: 'string',
  },
  {
    name: 'Address Locality',
    key: 'locality',
    required: false,
    description: 'The locality of the user',
    suggested_mappings: ['Locality', 'City'],
    data_type: 'string',
  },
  {
    name: 'Address Country Code',
    key: 'country_code',
    required: false,
    description: 'The country of the user',
    suggested_mappings: ['Country Code'],
    data_type: 'string',
  },
] as const satisfies ReadonlyArray<ColumnType>;

const translateSkipReason = (
  code: string | null | undefined,
  t: (key: string) => string,
): string => {
  if (code == null || code === '') {
    return '';
  }
  switch (code) {
    case 'notes_already_imported':
      return t('Notes already imported');
    case 'contact_exists_no_note':
      return t('Contact already exists — no note to import');
    default:
      return code;
  }
};

export const UsersImport = (props: UsersImportProps) => {
  const { t } = useLocale();

  return (
    <CSVImportModal
      objectLabel="Users"
      open={props.open}
      columns={columns}
      onImportSuccess={props.onImportSuccess}
      onImporterClose={props.onImporterClose}
      mutationQuery={IMPORT_USERS}
      constructMutationVariables={formattedData => ({
        objects: formattedData,
      })}
      renderSuccessContent={data => {
        const skippedRows = data.import_users?.filter(row => row.skipped) ?? [];
        if (skippedRows.length === 0) {
          return null;
        }
        return (
          <Alert severity="warning">
            <AlertTitle>{t('The following rows were skipped:')}</AlertTitle>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {skippedRows.map((row, index) => (
                <Box component="li" key={`${row.label}-${index}`}>
                  <strong>{row.label}</strong>
                  {' — '}
                  {translateSkipReason(row.skip_reason, t)}
                </Box>
              ))}
            </Box>
          </Alert>
        );
      }}
    />
  );
};

export default UsersImport;
